import { LoginFormV2 } from "@components/forms/LoginFormV2";
import { PageHead } from "@templates/Page/PageHead";
import { Onboarding } from "@templates/Onboarding";
import { enqueueSnackbar } from "notistack";
import { useRouter } from "next/router";
import log from "@services/Log";
import Amplitude from "@services/Amplitude";
import { AUTH_OVERRIDE_EMAILS } from "@assets/constants";
import { useEffect } from "react";
import Auth from "@services/Auth";
import publicApi from "@api/publicApi";
import { useRecoilValue } from "recoil";
import { sessState, userState } from "@recoil/atoms";
import { decideStartPage } from "@lib/decideStartPage";
import { useSearchParams } from "next/navigation";
import { getRecoil } from "recoil-nexus";

type PageProps = {
  error: string;
  message: string;
};
export default function SigninPage() {
  const router = useRouter();
  const user = useRecoilValue(userState);
  const searchParams = useSearchParams();
  const message = searchParams.get("message");
  const error = searchParams.get("error");

  useEffect(() => {
    if (!getRecoil(sessState)) return;
    publicApi
      .get("/auth/credentials", {
        // allows this call to utilize cookies set on the auth server:
        headers: {
          Authorization: `Bearer ${getRecoil(sessState)}`,
        },
      })
      .then((token) => {
        if (token && user) {
          const path = decideStartPage(user, false);
          if (path) router.push(path);
        }
      })
      .catch((e) => {
        // nothing, they may not be logged in
      });
  }, []);

  // handle login failures
  useEffect(() => {
    if (message) {
      enqueueSnackbar({
        variant: "info",
        title: "Info",
        subtitle: message,
      });
    }
    if (error) {
      enqueueSnackbar({
        variant: "error",
        title: "Error",
        subtitle: error,
      });
    }
  }, [message, error]);

  const onSignIn = async (values: { email: string }) => {
    try {
      Amplitude.logEvent("User pressed sign in button", { email: values?.email });
      const email = values.email;

      await Auth.requestMagicLink(email);
      if (AUTH_OVERRIDE_EMAILS.includes(email)) {
        // bypass magic link for certain emails; this token does not expire.
        router.push(`${process.env.NEXT_PUBLIC_ORIGIN}/?token=674a86e5-3bb2-4c77`);
      } else {
        router.push({ pathname: "/o/inbox", query: { email } });
      }
    } catch (error: any) {
      enqueueSnackbar({
        variant: "error",
        title: error?.message,
        subtitle: "Try again or contact us at support@astronaut.chat for help",
      });
      log.error(error, error?.message, { values });
    }
  };

  return (
    <Onboarding shouldCenterAlignBody>
      <PageHead title={"Sign in"} />
      <LoginFormV2
        onSave={onSignIn}
        type="login"
      />
    </Onboarding>
  );
}
